import { inkOf, tintOf, vmColor } from "@helpers/themeColor"
import { SparklesIcon } from "@heroicons/react/24/outline"
import { useTranslation } from "react-i18next"

type Point = { x: number; y: number }

const CENTER = { x: 170, y: 120 }
// NOTE: Branch colors are the illustration's own; fills and text follow the
// light/dark palette.
const CENTER_COLOR = vmColor("primary")
const BRANCH_WIDTH = 108
const BRANCH_HEIGHT = 34
const LEAF_WIDTH = 66
const LEAF_HEIGHT = 22

const BRANCHES = [
  {
    color: vmColor("primary"),
    emoji: "🎯",
    key: "branch1",
    leaves: [
      { key: "leaf1a", x: 42, y: 20 },
      { key: "leaf1b", x: 116, y: 20 },
    ],
    x: 80,
    y: 64,
  },
  {
    color: vmColor("green"),
    emoji: "🧠",
    key: "branch2",
    leaves: [
      { key: "leaf2a", x: 224, y: 20 },
      { key: "leaf2b", x: 298, y: 20 },
    ],
    x: 260,
    y: 64,
  },
  {
    color: vmColor("yellow"),
    emoji: "✍️",
    key: "branch3",
    leaves: [
      { key: "leaf3a", x: 42, y: 220 },
      { key: "leaf3b", x: 116, y: 220 },
    ],
    x: 80,
    y: 176,
  },
  {
    color: vmColor("red"),
    emoji: "😴",
    key: "branch4",
    leaves: [
      { key: "leaf4a", x: 224, y: 220 },
      { key: "leaf4b", x: 298, y: 220 },
    ],
    x: 260,
    y: 176,
  },
]

const NODE_STYLE = {
  transformBox: "fill-box",
  transformOrigin: "center",
} as const

function horizontalCurve(from: Point, to: Point) {
  const midX = (from.x + to.x) / 2

  return `M${from.x} ${from.y} C ${midX} ${from.y}, ${midX} ${to.y}, ${to.x} ${to.y}`
}

function verticalCurve(from: Point, to: Point) {
  const midY = (from.y + to.y) / 2

  return `M${from.x} ${from.y} C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y}`
}

function YouTubeCard() {
  const { t } = useTranslation()

  return (
    <div className="vm-card flex w-full animate-fade-up items-center gap-3 p-2.5">
      <div className="relative h-[60px] w-[106px] shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-vm-primary-soft via-vm-primary to-vm-primary-dark">
        <span className="absolute -left-1 top-1 text-3xl opacity-90">🧠</span>
        <span className="absolute bottom-1 left-12 text-lg opacity-80">💡</span>
        <span className="absolute left-1/2 top-1/2 flex h-6 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg bg-[#FF0000] shadow-md">
          <span className="ml-0.5 h-0 w-0 border-y-[5px] border-l-[8px] border-y-transparent border-l-white" />
        </span>
        <span className="absolute bottom-1 right-1 rounded bg-black/80 px-1 text-[9px] font-semibold text-white">
          42:17
        </span>
      </div>

      <div className="min-w-0">
        <p className="line-clamp-2 text-sm font-semibold leading-snug text-vm-ink">
          {t("pages.onboarding.visuals.mindMapDemo.videoTitle")}
        </p>
        <p className="mt-1 flex items-center gap-1 text-xs text-vm-muted">
          <span className="flex h-3 w-4 items-center justify-center rounded-[3px] bg-[#FF0000]">
            <span className="ml-px h-0 w-0 border-y-[2.5px] border-l-[4px] border-y-transparent border-l-white" />
          </span>
          YouTube
        </p>
      </div>
    </div>
  )
}

function MappingStep() {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center">
      <span className="h-3 w-px animate-fade-in bg-vm-primary/30 [animation-delay:200ms]" />
      <span className="relative flex animate-scale-in items-center gap-1.5 overflow-hidden rounded-full bg-vm-primary-solid px-3 py-1 text-xs font-semibold text-white shadow-cta [animation-delay:300ms]">
        <span className="absolute inset-0 animate-shine bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        <SparklesIcon className="relative h-3.5 w-3.5" />
        <span className="relative">
          {t("pages.onboarding.visuals.mindMapDemo.mapping")}
        </span>
      </span>
      <span className="h-3 w-px animate-fade-in bg-vm-primary/30 [animation-delay:500ms]" />
    </div>
  )
}

export default function MindMapVisual() {
  const { t } = useTranslation()

  return (
    <div className="flex w-full flex-col items-center">
      <YouTubeCard />
      <MappingStep />

      <div className="vm-card w-full overflow-hidden bg-[radial-gradient(rgb(var(--vm-line))_1px,transparent_1px)] p-2 [background-size:14px_14px]">
        <svg className="w-full overflow-visible" viewBox="0 0 340 240">
          <defs>
            <linearGradient id="mind-map-center" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#8B7DFF" />
              <stop offset="100%" stopColor="#6A5AF9" />
            </linearGradient>
            <filter
              height="200%"
              id="mind-map-glow"
              width="200%"
              x="-50%"
              y="-50%"
            >
              <feGaussianBlur stdDeviation="8" />
            </filter>
            <filter
              height="180%"
              id="mind-map-shadow"
              width="140%"
              x="-20%"
              y="-40%"
            >
              <feDropShadow
                dx="0"
                dy="2"
                floodOpacity="0.08"
                style={{ floodColor: vmColor("backdrop") }}
                stdDeviation="2.5"
              />
            </filter>
            {BRANCHES.map((branch) => (
              <linearGradient
                gradientUnits="userSpaceOnUse"
                id={`mind-map-${branch.key}`}
                key={branch.key}
                x1={CENTER.x}
                x2={branch.x}
                y1={CENTER.y}
                y2={branch.y}
              >
                <stop offset="0%" style={{ stopColor: CENTER_COLOR }} />
                <stop offset="100%" style={{ stopColor: branch.color }} />
              </linearGradient>
            ))}
          </defs>

          {BRANCHES.map((branch, index) => {
            const edgeY =
              branch.leaves[0].y < branch.y
                ? branch.y - BRANCH_HEIGHT / 2
                : branch.y + BRANCH_HEIGHT / 2

            return (
              <g key={branch.key}>
                <path
                  className="animate-draw"
                  d={horizontalCurve(CENTER, branch)}
                  fill="none"
                  pathLength={1}
                  stroke={`url(#mind-map-${branch.key})`}
                  strokeDasharray="1"
                  strokeOpacity="0.6"
                  strokeWidth="3"
                  style={{
                    animationDelay: `${900 + index * 150}ms`,
                    animationDuration: "0.6s",
                  }}
                />
                {branch.leaves.map((leaf, leafIndex) => (
                  <path
                    className="animate-draw"
                    d={verticalCurve(
                      { x: branch.x, y: edgeY },
                      {
                        x: leaf.x,
                        y:
                          leaf.y < branch.y
                            ? leaf.y + LEAF_HEIGHT / 2
                            : leaf.y - LEAF_HEIGHT / 2,
                      },
                    )}
                    fill="none"
                    key={leaf.key}
                    pathLength={1}
                    strokeDasharray="1"
                    strokeOpacity="0.4"
                    strokeWidth="1.5"
                    style={{
                      stroke: branch.color,
                      animationDelay: `${1700 + index * 140 + leafIndex * 70}ms`,
                      animationDuration: "0.4s",
                    }}
                  />
                ))}
              </g>
            )
          })}

          <g
            className="animate-scale-in [animation-delay:700ms]"
            style={NODE_STYLE}
          >
            <rect
              className="animate-pulse"
              filter="url(#mind-map-glow)"
              height="40"
              opacity="0.4"
              rx="20"
              style={{ fill: CENTER_COLOR }}
              width="128"
              x={CENTER.x - 64}
              y={CENTER.y - 18}
            />
            <rect
              fill="url(#mind-map-center)"
              height="40"
              rx="20"
              width="128"
              x={CENTER.x - 64}
              y={CENTER.y - 20}
            />
            <rect
              fill="none"
              height="38"
              rx="19"
              stroke="#fff"
              strokeOpacity="0.25"
              width="126"
              x={CENTER.x - 63}
              y={CENTER.y - 19}
            />
            <text
              fill="#fff"
              fontSize="13"
              fontWeight="700"
              textAnchor="middle"
              x={CENTER.x}
              y={CENTER.y + 4.5}
            >
              {t("pages.onboarding.visuals.mindMapDemo.center")}
            </text>
          </g>

          {BRANCHES.map((branch, index) => {
            const left = branch.x - BRANCH_WIDTH / 2

            return (
              <g key={branch.key}>
                <g
                  className="animate-scale-in"
                  style={{
                    ...NODE_STYLE,
                    animationDelay: `${1200 + index * 150}ms`,
                  }}
                >
                  <rect
                    filter="url(#mind-map-shadow)"
                    height={BRANCH_HEIGHT}
                    rx={BRANCH_HEIGHT / 2}
                    strokeOpacity="0.22"
                    style={{ fill: vmColor("cream"), stroke: branch.color }}
                    width={BRANCH_WIDTH}
                    x={left}
                    y={branch.y - BRANCH_HEIGHT / 2}
                  />
                  <circle
                    cx={left + 17}
                    cy={branch.y}
                    r="12"
                    style={{ fill: tintOf(branch.color) }}
                  />
                  <text
                    fontSize="12"
                    textAnchor="middle"
                    x={left + 17}
                    y={branch.y + 4.2}
                  >
                    {branch.emoji}
                  </text>
                  <text
                    fontSize="11.5"
                    fontWeight="700"
                    style={{ fill: vmColor("ink") }}
                    x={left + 35}
                    y={branch.y + 4}
                  >
                    {t(`pages.onboarding.visuals.mindMapDemo.${branch.key}`)}
                  </text>
                </g>

                {branch.leaves.map((leaf, leafIndex) => (
                  <g
                    className="animate-scale-in"
                    key={leaf.key}
                    style={{
                      ...NODE_STYLE,
                      animationDelay: `${1900 + index * 140 + leafIndex * 70}ms`,
                    }}
                  >
                    <rect
                      height={LEAF_HEIGHT}
                      rx={LEAF_HEIGHT / 2}
                      style={{ fill: tintOf(branch.color) }}
                      width={LEAF_WIDTH}
                      x={leaf.x - LEAF_WIDTH / 2}
                      y={leaf.y - LEAF_HEIGHT / 2}
                    />
                    <text
                      fontSize="9.5"
                      fontWeight="600"
                      style={{ fill: inkOf(branch.color) }}
                      textAnchor="middle"
                      x={leaf.x}
                      y={leaf.y + 3.3}
                    >
                      {t(`pages.onboarding.visuals.mindMapDemo.${leaf.key}`)}
                    </text>
                  </g>
                ))}
              </g>
            )
          })}
        </svg>
      </div>
    </div>
  )
}
